import React from 'react'
import { z } from 'zod'
import { CheckoutEvents } from '@mc/pre-paywall-events'
import { EventTypes } from '@mc/track-event'
import { createTracking } from '@mc/tracking-kit'

const goldStandardOptions = {
  integrations: {
    All: true,
    'Bing Ads': false,
    'Facebook CAPI': false,
    'Facebook Pixel': false,
    'Google AdWords New': false,
    'Google Tag Manager': true,
    Hotjar: false,
    ShareASale: false,
    TikTok: false,
  },
}

const pageSource = z.enum([
  'Certificate Hub Landing',
  'Certificate Hub',
  'Certificate Subscriptions Landing',
  'Certificate Overview Landing',
  'Certificate Cohort Landing',
  'Certificate Topic Landing',
  'Certificate Quiz',
  'Certificate Checkout',
  'Checkout Step: Value Primer',
  'Checkout Step: Account Creation',
  'Checkout Step: Payment Entry',
  'Checkout Step: Credit Card Input',
  'Certificate Post Purchase Add-on Screen',
  'Certificate Post Purchase Add-on Thanks Screen',
  'Certificate Overview',
  'Certificate Lesson',
  'Certificate Completion',
  'Certificate Account Creation',
  'Certificate Toolkit',
  'Certificate Projects',
  'Referral Sources',
  'Audio Referral Sources',
])

export type CertificatePageSource = z.infer<typeof pageSource>

const commonSchema = {
  page_source: pageSource,
  product: z.enum(['certificates', 'Certificate Topics']),
  certificate_id: z.string().optional(),
  stack_id: z.string().optional(),
  // Subscriptions entry-point spec (MCCERTS-2635): identifies which SKU type
  // the user is converting on; a subscription order carries no certificate_id
  plan_type: z.enum(['individual', 'subscription']).optional(),
  // Add-Ons spec: the product the user bought before the add-on was offered
  base_product_id: z.string().optional(),
  base_product_name: z.string().optional(),
  // Experiment assignment passthrough required on all events in the spec
  experiment_id: z.string().optional(),
  variation_id: z.string().optional(),
  // Expiration spec: entitlement status, set once at each post-paywall page root
  access_state: z.enum(['live', 'expired', 'consent_required']).optional(),
  // Enterprise data-sharing consent (FLW-1234): the org that owns the entitlement
  organization_id: z.number().optional(),
}

const locations = z.enum([
  'hero',
  'hero-headline',
  'essentials',
  'explore-certificates',
  'mc-brand-trust',
  'what-you-get',
  'certificate-rewards',
  'certificate-news-email-capture',
  'testimonials',
  'certificate-learning-experience',
  'certificate-plans-primer-page',
  'plans-selection',
  'plans-selection-sticky-cta',
  'FAQs',
  'meet-your-instructor',
  'certificate-overview',
  'enroll-prompt',
  'certificate trailer end screen',
  'footer',
  'drop',
  'free-trial',
  'promo banner',
  'promo modal',
  'enroll welcome modal',
  'promo footer',
  'nav bar',
  'continue-certificate',
  'certificate-hero',
  'certificate-outcomes',
  'certificate-module',
  'certificate-requirements',
  'program-outline',
  'bottom-nav',
  'discussion',
  'quiz',
  'quiz-results-card',
  'quiz-results-sticky-cta',
  'Download',
  'certificate-completion',
  'capstone-summary',
  'certificate-drops-showcase',
  'checkout',
  'navigation',
  'dropdown-navigation',
  'who-is-this-for',
  'ai-feedback',
  'capstone-rubric',
  'self-assessment',
  'glossary',
  'offering-not-available-modal',
  'sticky-footer',
  'sidepanel',
  'linkedin-testimonials',
  'applied-learning',
  'challenge-status-complete',
  'roleplay',
  'roleplay-rubric',
  'roleplay-feedback',
  'preview-videos',
  'certificate_webinar_banner',
  'choose-your-plan',
  'syllabus-banner',
  'curriculum-logos',
  'toolkit-tool-panel',
  'toolkit-summary',
  'overview-recent-tools',
  'overview-recent-projects',
  'overview-download',
  'projects-panel',
  'lesson-tool-panel',
  'certificate-sub-prompt', // mccerts-2548_v1-subscriptions-entry-point
  'hero-plan-selector', // mccerts-2548_v1-subscriptions-entry-point
  'hero-simple-sub', // mccerts-2548_v1-subscriptions-entry-point
  'hero-subs-only-annual', // mccerts-2972-subs-only-clp-v1
  'hero-subs-only-monthly', // mccerts-2972-subs-only-clp-v1
  'hero-subs-only-no-price', // mccerts-2972-subs-only-clp-v1
  'enterprise_sponsor_row', // mccerts-2784_v1-certificates-for-teams
  'overview-hero',
  'tools-preview',
  'projects-preview',
  'share-project',
  'left-nav-bar',
  'toolkit',
  'toolkit-sidepanel',
  'project-sidepanel',
  'course-access-expired',
  'expired-extension',
  'expired-upsell',
  'cohort-partners',
  'cohort-curriculum',
])

// mccerts-2548_v1-subscriptions-entry-point v1/v2
const planView = z.enum(['individual', 'subscription'])

// mccerts-2972-subs-only-clp-v1: the pass price state the hero rendered
const heroState = z.enum(['promo', 'default'])

// mccerts-2972-subs-only-clp-v1: the GrowthBook arm, as on Experiment Viewed
const subsOnlyClpVariant = z.enum([
  'control',
  'variant_1',
  'variant_2',
  'variant_3',
])

const certificatesSchema = {
  productViewed: {
    eventName: EventTypes.PRODUCT_VIEWED_GOLD_STANDARD,
    schema: z.object({
      product_id: z.number(),
      price: z.number(),
      name: z.string(),
      currency: z.string(),
      content_type: z.string().default('product'),
      quantity: z.number().default(1),
      ...commonSchema,
    }),
    options: goldStandardOptions,
  },
  certificateLandingClick: {
    eventName: 'Certificate Landing Click',
    schema: z.object({
      action: z.string().optional(),
      location: locations,
      href: z.string().optional(),
      product_name: z.string().optional(),
      level: z.enum(['primary', 'secondary', 'tertiary']).optional(),
      default_view: planView.optional(), // mccerts-2548_v1-subscriptions-entry-point
      ...commonSchema,
    }),
  },
  browseCertificatesClick: {
    eventName: 'Certificate Landing Click',
    schema: z.object({
      action: z.string(),
      location: locations,
      href: z.string().optional(),
      row_name: locations,
      row_position_index: z.number(),
      tile_position_index: z.number(),
      tile_title: z.string(),
      tile_subtitle: z.string(),
      tile_link: z.string().optional(),
      product_name: z.string().optional(),
      ...commonSchema,
    }),
  },
  certificateLogoLoadFailed: {
    eventName: 'Certificate Logo Load Failed',
    schema: z.object({
      logo_url: z.string(),
      tile_title: z.string(),
      ...commonSchema,
    }),
  },
  browseCertificatesClickLoggedIn: {
    eventName: 'Certificate Click',
    schema: z.object({
      action: z.string(),
      location: locations,
      row_name: locations,
      row_position_index: z.number(),
      tile_position_index: z.number(),
      tile_title: z.string(),
      tile_subtitle: z.string(),
      tile_link: z.string().optional(),
      ...commonSchema,
    }),
  },
  aiFeedbackModalViewed: {
    eventName: 'Certificate Modal Viewed',
    schema: z.object({
      modal_type: z.string().default('ai_feedback'),
      ...commonSchema,
    }),
  },
  aiFeedbackModalClicked: {
    eventName: 'Certificate Modal Clicked',
    schema: z.object({
      action: z.string(),
      modal_type: z.string().default('ai_feedback'),
      comment: z.string().optional(),
      ...commonSchema,
    }),
  },
  syllabusModalViewed: {
    eventName: 'Certificate Modal Viewed',
    schema: z.object({
      modal_type: z.string().default('syllabus modal'),
      ...commonSchema,
    }),
  },
  syllabusModalClicked: {
    eventName: 'Modal Clicked',
    schema: z.object({
      action: z.string(),
      modal_type: z.string().default('syllabus modal'),
      resource_type: z.string().default('program_pdf_syllabus'),
      resource_link: z.string(),
      resource_title: z.string(),
      ...commonSchema,
    }),
  },
  // Expiration analytics spec: fired when an expired-state module enters the viewport
  certificateExpiredStateViewed: {
    eventName: 'Certificate Expired State Viewed',
    schema: z.object({
      ...commonSchema,
      location: locations,
      access_state: z.literal('expired'),
      days_since_expiration: z.number().int().nonnegative().optional(),
    }),
  },
  certificateRowViewed: {
    eventName: 'Certificate Row Viewed',
    schema: z.object({
      row_name: locations,
      row_position_index: z.number(),
      column_position_index: z.number().optional(),
      product_name: z.string().optional(),
      default_view: planView.optional(), // mccerts-2548_v1-subscriptions-entry-point
      hero_state: heroState.optional(), // mccerts-2972-subs-only-clp-v1
      ...commonSchema,
    }),
  },
  certificateToolRowViewed: {
    eventName: 'Certificate Row Viewed',
    schema: z.object({
      row_name: z.string(),
      row_position_index: z.number(),
      tool_type: z.string(),
      tool_name: z.string(),
      product_name: z.string().optional(),
      ...commonSchema,
    }),
  },
  certificateRecentRowTileViewed: {
    eventName: 'Certificate Row Tile Viewed',
    schema: z.object({
      row_name: z.string(),
      row_position_index: z.number(),
      tile_title: z.string(),
      tile_subtitle: z.string().optional(),
      tile_link: z.string().optional(),
      column_position_index: z.number(),
      tile_position_index: z.number(),
      product_name: z.string().optional(),
      ...commonSchema,
    }),
  },
  certificateProjectRowViewed: {
    eventName: 'Certificate Row Viewed',
    schema: z.object({
      row_name: z.string(),
      row_position_index: z.number(),
      project_name: z.string(),
      product_name: z.string().optional(),
      ...commonSchema,
    }),
  },
  certificateUnitViewed: {
    eventName: 'Certificate Row Viewed',
    schema: z
      .object({
        row_position_index: z.number(),
        ...commonSchema,
      })
      .passthrough(),
  },
  certificateUnitLessonViewed: {
    eventName: 'Certificate Row Tile Viewed',
    schema: z
      .object({
        row_position_index: z.number(),
        ...commonSchema,
      })
      .passthrough(),
  },
  certificateRowTileViewed: {
    eventName: 'Certificate Row Tile Viewed',
    schema: z.object({
      row_name: locations,
      row_position_index: z.number(),
      tile_position_index: z.number(),
      tile_title: z.string(),
      tile_subtitle: z.string(),
      tile_link: z.string().optional(),
      product_name: z.string().optional(),
      ...commonSchema,
    }),
  },
  certificateEmailCaptured: {
    eventName: 'Certificate Email Captured',
    schema: z.object({
      action: z.string(),
      location: z.enum(['coming soon', 'syllabus', 'general', 'checkout']),
      email: z.string(),
      campaign: z.string(),
      gdpr_opt_in: z.boolean().optional(),
      marketing_email_consent: z.boolean().optional(),
      ...commonSchema,
    }),
  },
  syllabusDownloaded: {
    eventName: 'Syllabus Downloaded',
    schema: z.object({
      location: z.enum(['syllabus', 'syllabus-banner']),
      email: z.string(),
      campaign: z.string(),
      syllabus_link: z.string(),
      product_name: z.string().optional(),
      ...commonSchema,
    }),
    options: goldStandardOptions,
  },
  purchaseClick: {
    eventName: 'Purchase Click',
    schema: z.object({
      action: z.string(),
      location: locations,
      product_name: z.string().optional(),
      default_view: planView.optional(), // mccerts-2548_v1-subscriptions-entry-point
      hero_state: heroState.optional(), // mccerts-2972-subs-only-clp-v1
      variant: subsOnlyClpVariant.optional(), // mccerts-2972-subs-only-clp-v1
      ...commonSchema,
    }),
    options: goldStandardOptions,
  },
  certificateRowScrolled: {
    eventName: 'Certificate Row Scrolled',
    schema: z.object({
      direction: z.enum(['right', 'left']),
      row_name: locations,
      row_position_index: z.number(),
      ...commonSchema,
    }),
  },
  certificateNavigationClicked: {
    eventName: 'Certificate Navigation Clicked',
    schema: z.object({
      action: z.string(),
      level: z.enum(['primary', 'secondary', 'tertiary', 'quaternary']),
      location: z.enum(['nav bar', 'footer', 'left-nav-bar']),
      href: z.string().optional(),
      product_name: z.string().optional(),
      ...commonSchema,
    }),
  },
  checkoutCtaClicked: {
    eventName: 'Checkout CTA Clicked',
    schema: z.object({
      action: z.string(),
      step_name: z.string(),
      product_name: z.string(),
      ...commonSchema,
    }),
  },
  certificatePlansSelect: {
    eventName: 'Certificate Checkout Click',
    schema: z.object({
      action: z.string(),
      location: locations,
      product_name: z.string(),
      ...commonSchema,
    }),
  },
  certificatePrimerScreenViewed: {
    eventName: 'Certificate Primer Screen Viewed',
    schema: z.object({
      product_name: z.string(),
      ...commonSchema,
    }),
  },
  certificatePrimerContinue: {
    eventName: 'Certificate Primer Screen CTA Clicked',
    schema: z.object({
      action: z.string(),
      location: locations,
      product_name: z.string(),
      ...commonSchema,
    }),
  },
  certificatePrimerAbandoned: {
    eventName: 'Certificate Primer Screen Abandoned',
    schema: z.object({
      time_on_screen: z.number(),
      product_name: z.string(),
      ...commonSchema,
    }),
  },
  certificateQuizEntered: {
    eventName: 'Certificate Quiz Entered',
    schema: z.object({
      source: z.literal('checkout_step'),
      step: z.literal('goal'),
      location: locations,
      ...commonSchema,
    }),
  },
  certificateQuizScreenViewed: {
    eventName: 'Certificate Quiz Screen Viewed',
    schema: z.object({
      screen: z.string(),
      location: locations,
      ...commonSchema,
    }),
  },
  certificateQuizQuestionAnswered: {
    eventName: 'Certificate Quiz Question Answered',
    schema: z.object({
      step: z.string(),
      option_id: z.string(),
      location: locations,
      ...commonSchema,
    }),
  },
  certificateQuizBackClicked: {
    eventName: 'Certificate Quiz Back Clicked',
    schema: z.object({
      screen: z.string(),
      location: locations,
      ...commonSchema,
    }),
  },
  certificateQuizAbandoned: {
    eventName: 'Certificate Quiz Abandoned',
    schema: z.object({
      last_screen: z.string(),
      goal: z.string().nullable(),
      role: z.string().nullable(),
      level: z.string().nullable(),
      time: z.string().nullable(),
      location: locations,
      ...commonSchema,
    }),
  },
  certificateQuizResultsViewed: {
    eventName: 'Certificate Quiz Results Viewed',
    schema: z.object({
      goal: z.string().nullable(),
      role: z.string().nullable(),
      level: z.string().nullable(),
      time: z.string().nullable(),
      profile: z.string(),
      location: locations,
      ...commonSchema,
    }),
  },
  certificateQuizResultsCtaClicked: {
    eventName: 'Certificate Quiz Results CTA Clicked',
    schema: z.object({
      action: z.string(),
      location: locations.extract([
        'quiz-results-card',
        'quiz-results-sticky-cta',
      ]),
      href: z.string(),
      ...commonSchema,
    }),
  },
  certificatePlansScreenViewed: {
    eventName: 'Certificate Plans Screen Viewed',
    schema: z.object({
      product_name: z.string(),
      ...commonSchema,
    }),
  },
  plansTooltipViewed: {
    eventName: 'Certificate Plans Tooltip Viewed',
    schema: z.object({
      feature_key: z.string(),
      tooltip_text: z.string(),
      product_name: z.string(),
      ...commonSchema,
    }),
  },
  certificatePlansContinue: {
    eventName: 'Certificate Checkout Step Completed',
    schema: z.object({
      action: z.string(),
      tier: z.string(),
      location: locations,
      product_name: z.string(),
      ...commonSchema,
    }),
  },
  promoModalViewed: {
    eventName: 'Modal Viewed',
    schema: z.object({
      modal_type: z.string(),
      ...commonSchema,
    }),
  },
  promoModalClicked: {
    eventName: 'Modal Clicked',
    schema: z.object({
      action: z.string(),
      modal_type: z.string(),
      ...commonSchema,
    }),
  },
  productAdded: {
    eventName: CheckoutEvents.EVENTS.PRODUCT_ADDED,
    schema: z
      .object({
        // Optional: stacks carry stack_id (via context/passthrough) instead of
        // certificate_id. Single certs still supply certificate_id.
        certificate_id: z.string().optional(),
      })
      .passthrough(), // pass through properties from BE
    options: goldStandardOptions,
  },
  cartEmailCaptured: {
    eventName: CheckoutEvents.EVENTS.CART_EMAIL_CAPTURED_GOLD_STANDARD,
    schema: z
      .object({
        content_type: z.string().default('product'),
        product_id: z.string(),
        email: z.string(),
        marketing_consent: z.boolean().optional(),
        gdpr_opt_in: z.boolean().optional(),
        email_capture_method: z.literal('user_added'),
        ...commonSchema,
      })
      .passthrough(), // pass through properties from BE
    options: goldStandardOptions,
  },
  infoStepViewed: {
    eventName: CheckoutEvents.EVENTS.PAYMENT_INFO_STEP_VIEWED_GOLD_STANDARD,
    schema: z
      .object({
        ...commonSchema,
      })
      .passthrough(), // pass through properties from BE
    options: goldStandardOptions,
  },
  orderCompleted: {
    eventName: 'Order Completed',
    schema: z.object({}).passthrough(), // pass through properties from BE
    options: goldStandardOptions,
  },
  addOnScreenClicked: {
    eventName: 'Certificate Post Purchase Add-on Screen Clicked',
    schema: z.object({
      action: z.string(),
      product_name: z.string(),
      price: z.number(),
      currency: z.string(),
      ...commonSchema,
    }),
  },
  addOnThanksScreenClicked: {
    eventName: 'Certificate Post Purchase Add-on Thanks Screen Clicked',
    schema: z.object({
      action: z.string(),
      product_name: z.string(),
      price: z.number(),
      currency: z.string(),
      ...commonSchema,
    }),
  },
  certificateUnitTileClick: {
    eventName: 'Certificate Click',
    schema: z
      .object({
        action: z.string(),
        ...commonSchema,
      })
      .passthrough(),
  },
  certificateClick: {
    eventName: 'Certificate Click',
    schema: z.object({
      action: z.string(),
      location: locations,
      // Expiration spec: "30pctoff" | "free", the offer the click refers to
      discount: z.string().optional(),
      unit_id: z.string().optional(),
      lesson_id: z.string().optional(),
      activity_id: z.string().optional(),
      row_name: z.string().optional(),
      row_position_index: z.number().optional(),
      column_position_index: z.number().optional(),
      tile_link: z.string().optional(),
      tile_position_index: z.number().optional(),
      tile_title: z.string().optional(),
      quizResponses: z.object({}).passthrough().optional(),
      href: z.string().optional(),
      resource_type: z.string().optional(),
      product_name: z.string().optional(),
      location_type: z.string().optional(),
      conversation_id: z.string().optional(),
      ...commonSchema,
    }),
  },
  certificateSidePanelViewed: {
    eventName: 'Certificate Side Panel Viewed',
    schema: z.object({
      sidepanel_type: z.enum([
        'roleplay',
        'roleplay-rubric',
        'roleplay-feedback',
        'capstone-rubric',
        'capstone-summary',
        'ai-feedback',
        'self-assessment',
        'applied-learning',
        'glossary',
        'tool',
        'project',
      ]),
      location: locations.optional(),
      tool_id: z.string().optional(),
      tool_type: z.string().optional(),
      is_personalized: z.boolean().optional(),
      unit_id: z.string().optional(),
      lesson_id: z.string().optional(),
      activity_id: z.string().optional(),
      ...commonSchema,
    }),
  },
  certificateRoleplayCustomized: {
    eventName: 'Certificate Roleplay Customized',
    schema: z.object({
      conversation_id: z.string().optional(),
      activity_id: z.string().optional(),
      ai_role: z.string(),
      voice_style: z.string(),
      ai_personality: z.string(),
      has_additional_context: z.boolean(),
      additional_context: z.string().optional(),
      unit_id: z.string().optional(),
      lesson_id: z.string().optional(),
      ...commonSchema,
    }),
  },
  certificateRoleplayViolation: {
    eventName: 'Certificate Roleplay Violation',
    schema: z.object({
      conversation_id: z.string(),
      unit_id: z.string().optional(),
      lesson_id: z.string().optional(),
      ...commonSchema,
    }),
  },
  lessonResourceDownloadClick: {
    eventName: 'Certificate Click',
    schema: z
      .object({
        action: z.string(),
        location: locations,
        resource_type: z.string(),
        resource_title: z.string(),
        unit_id: z.string().optional(),
        lesson_id: z.string().optional(),
        activity_id: z.string().optional(),
        ...commonSchema,
      })
      .passthrough(),
  },
  certificateQuizSubmitted: {
    eventName: 'Certificate Quiz Submitted',
    schema: z.object({
      score: z.number(),
      passed: z.boolean(),
      responses: z.object({}).passthrough().optional(),
      unit_id: z.string().optional(),
      lesson_id: z.string().optional(),
      activity_id: z.string().optional(),
      ...commonSchema,
    }),
  },
  certificateQuestionnaireSubmitted: {
    eventName: 'Certificate Questionnaire Submitted',
    schema: z.object({
      unit_id: z.string().optional(),
      lesson_id: z.string().optional(),
      activity_id: z.string().optional(),
      ...commonSchema,
    }),
  },
  outlineUnitViewed: {
    eventName: 'Certificate Row Viewed',
    schema: z
      .object({
        location: locations,
        lesson_id: z.string(),
        ...commonSchema,
      })
      .passthrough(),
  },
  outlineLessonViewed: {
    eventName: 'Certificate Row Tile Viewed',
    schema: z
      .object({
        location: locations,
        ...commonSchema,
      })
      .passthrough(),
  },
  capstoneFeedbackGiven: {
    eventName: 'Capstone Feedback Given',
    schema: z.object({
      rating: z.enum(['positive', 'negative']),
      user_feedback: z.string(),
      submission_id: z.string().optional(),
      activity_id: z.string().optional(),
      lesson_id: z.string().optional(),
      ...commonSchema,
    }),
  },
  promoFooterViewed: {
    eventName: 'Sticky Footer Viewed',
    schema: z.object({
      product_name: z.string().optional(),
      ...commonSchema,
    }),
  },
  certificatesHamburgerMenuClicked: {
    eventName: 'Certificates Hamburger Menu Clicked',
    schema: z.object({
      ...commonSchema,
    }),
  },
  offeringNotAvailableModalViewed: {
    eventName: 'Offering Not Available Modal View',
    schema: z.object({
      product_id: z.string(),
      product_name: z.string(),
      location: locations,
      ...commonSchema,
    }),
  },
  offeringNotAvailableModalClicked: {
    eventName: 'Offering Not Available Modal Clicked',
    schema: z.object({
      action: z.string(),
      email: z.string().optional(),
      product_id: z.string(),
      product_name: z.string(),
      location: locations,
      campaign: z.string().optional(),
      marketing_email_consent: z.boolean().optional(),
      ...commonSchema,
    }),
  },
  stickyFooterViewed: {
    eventName: 'Sticky Footer Viewed',
    schema: z.object({
      product_name: z.string().optional(),
      ...commonSchema,
    }),
  },
  challengeOfferViewed: {
    eventName: 'Certificate Modal Viewed',
    schema: z.object({
      modal_type: z.enum(['challenge-offer-30-day', 'challenge-offer-multi']),
      ...commonSchema,
    }),
  },
  challengeOfferClicked: {
    eventName: 'Certificate Modal Clicked',
    schema: z.object({
      action: z.string(),
      modal_type: z.enum(['challenge-offer-30-day', 'challenge-offer-multi']),
      ...commonSchema,
    }),
  },
  challengeAccepted: {
    eventName: 'Challenge Accepted',
    schema: z.object({
      action: z.string(),
      auto_enrolled: z.boolean(),
      challenge_duration_days: z.number().int(),
      discount_offer_pct: z.number().int(),
      challenge_expiration_date: z.string(),
      modal_type: z.string(),
      ...commonSchema,
    }),
  },
  challengeStatusBannerViewed: {
    eventName: 'Certificate Modal Viewed',
    schema: z.object({
      days_remaining: z.number().int(),
      modal_type: z.string().default('challenge-status-banner'),
      ...commonSchema,
    }),
  },
  challengeStatusBannerClicked: {
    eventName: 'Certificate Modal Clicked',
    schema: z.object({
      action: z.string(),
      days_remaining: z.number().int(),
      modal_type: z.string().default('challenge-status-banner'),
      ...commonSchema,
    }),
  },
  challengeRewardViewed: {
    eventName: 'Certificate Challenge Reward Viewed',
    schema: z.object({
      ...commonSchema,
    }),
  },
  challengeContactSupportClick: {
    eventName: 'Certificate Click',
    schema: z.object({
      action: z.string(),
      location: locations,
      discount_offer_pct: z.number().int(),
      ...commonSchema,
    }),
  },
  challengeRewardBannerViewed: {
    eventName: 'Certificate Modal Viewed',
    schema: z.object({
      modal_type: z.string().default('challenge-reward-banner'),
      ...commonSchema,
    }),
  },
  challengeRewardBannerClicked: {
    eventName: 'Certificate Modal Clicked',
    schema: z.object({
      action: z.string(),
      modal_type: z.string().default('challenge-reward-banner'),
      ...commonSchema,
    }),
  },
  dataSharingConsentModalViewed: {
    eventName: 'Certificate Modal Viewed',
    schema: z.object({
      modal_type: z.string().default('data-sharing-consent-modal'),
      ...commonSchema,
    }),
  },
  dataSharingConsentModalClicked: {
    eventName: 'Certificate Modal Clicked',
    schema: z.object({
      action: z.string(),
      modal_type: z.string().default('data-sharing-consent-modal'),
      ...commonSchema,
    }),
  },
  enrollWelcomeModalViewed: {
    eventName: 'Certificate Modal Viewed',
    schema: z.object({
      modal_type: z.string().default('enroll-welcome-modal'),
      ...commonSchema,
    }),
  },
  enrollWelcomeModalClicked: {
    eventName: 'Certificate Modal Clicked',
    schema: z.object({
      action: z.string(),
      modal_type: z.string().default('enroll-welcome-modal'),
      ...commonSchema,
    }),
  },
  toolClick: {
    eventName: 'Certificate Click',
    schema: z.object({
      action: z.string(),
      tool_type: z.string(),
      tool_name: z.string(),
      row_position_index: z.number().optional(),
      location: z.string().optional(),
      unit_id: z.string().optional(),
      lesson_id: z.string().optional(),
      activity_id: z.string().optional(),
      product_name: z.string().optional(),
      ...commonSchema,
    }),
  },
  projectClick: {
    eventName: 'Certificate Click',
    schema: z.object({
      action: z.string(),
      project_name: z.string(),
      row_position_index: z.number().optional(),
      location: z.string().optional(),
      unit_id: z.string().optional(),
      lesson_id: z.string().optional(),
      activity_id: z.string().optional(),
      product_name: z.string().optional(),
      ...commonSchema,
    }),
  },
  certificateToolViewed: {
    eventName: 'Certificate Tool Viewed',
    schema: z.object({
      tool_type: z.string(),
      tool_name: z.string(),
      unit_id: z.string().optional(),
      lesson_id: z.string().optional(),
      activity_id: z.string().optional(),
      product_name: z.string().optional(),
      ...commonSchema,
    }),
  },
  projectSidepanelClick: {
    eventName: 'Certificate Click',
    schema: z.object({
      action: z.string(),
      location: locations,
      unit_id: z.string().optional(),
      lesson_id: z.string().optional(),
      activity_id: z.string().optional(),
      location_type: z.string().optional(),
      ...commonSchema,
    }),
  },
  toolSidepanelClick: {
    eventName: 'Certificate Click',
    schema: z.object({
      action: z.string(),
      location: locations,
      tool_name: z.string(),
      tool_type: z.string(),
      unit_id: z.string().optional(),
      lesson_id: z.string().optional(),
      activity_id: z.string().optional(),
      location_type: z.string().optional(),
      ...commonSchema,
    }),
  },
  toolUnlockedToastViewed: {
    eventName: 'Certificate Modal Viewed',
    schema: z.object({
      modal_type: z.string().default('celebration-small::tool-unlock'),
      tool_type: z.string(),
      tool_name: z.string(),
      unit_id: z.string().optional(),
      lesson_id: z.string().optional(),
      ...commonSchema,
    }),
  },
  toolUnlockedCelebrationViewed: {
    eventName: 'Certificate Modal Viewed',
    schema: z.object({
      modal_type: z.string().default('celebration-large::tool-unlock'),
      tool_type: z.string(),
      tool_name: z.string(),
      unit_id: z.string().optional(),
      lesson_id: z.string().optional(),
      ...commonSchema,
    }),
  },
  certificateModalClicked: {
    eventName: 'Certificate Modal Clicked',
    schema: z.object({
      action: z.string(),
      modal_type: z.string(),
      tool_type: z.string(),
      tool_name: z.string(),
      unit_id: z.string().optional(),
      lesson_id: z.string().optional(),
      ...commonSchema,
    }),
  },
}

const pageSchema = z
  .object({
    name: pageSource,
    product: z.union([
      z.literal('certificates'),
      z.literal('Certificate Topics'),
    ]),
    product_name: z.string().optional(),
    certificate_id: z.string().optional(),
    stack_id: z.string().optional(),
    activity_type: z.string().optional(),
    plan_type: z.enum(['individual', 'subscription']).optional(),
    base_product_id: z.string().optional(),
    base_product_name: z.string().optional(),
    experiment_id: z.string().optional(),
    variation_id: z.string().optional(),
    access_state: z.enum(['live', 'expired', 'consent_required']).optional(),
  })
  .passthrough() // pass through properties from BE

const {
  useFireEvent,
  TrackExperimentViewed,
  TrackClick,
  TrackPageViewed,
  TrackingAttributes,
  TrackOnMount,
  TrackVisible,
  TrackEvent,
} = createTracking(certificatesSchema, pageSchema)

const TrackGoldStandardPageViewed = (
  attributes: Parameters<typeof TrackPageViewed>[0],
) => <TrackPageViewed {...attributes} options={goldStandardOptions} />

// page_source and product are supplied by ancestor TrackingAttributes on every
// surface that opens a side panel, so callers only provide the rest.
export type SidePanelViewedProperties = Omit<
  z.input<typeof certificatesSchema.certificateSidePanelViewed.schema>,
  'page_source' | 'product'
>

export type SidePanelType = SidePanelViewedProperties['sidepanel_type']

export {
  useFireEvent,
  TrackClick,
  TrackExperimentViewed,
  TrackPageViewed,
  TrackGoldStandardPageViewed,
  TrackingAttributes,
  TrackOnMount,
  TrackVisible,
  TrackEvent,
}
